import { useParams, Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { movies, categories } from '@/mocks/movies';
import type { Movie } from '@/mocks/movies';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || 'dianying');
  const [filteredMovies, setFilteredMovies] = useState<Movie[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<'latest' | 'rating' | 'views'>('latest');

  const currentCategory = categories.find((c) => c.slug === activeCategory);

  useEffect(() => {
    if (slug) {
      setActiveCategory(slug);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [slug]);

  useEffect(() => {
    let result = movies.filter((m) => m.category === activeCategory);
    if (searchTerm) {
      result = result.filter(
        (m) =>
          m.title.includes(searchTerm) ||
          m.englishTitle.toLowerCase().includes(searchTerm.toLowerCase()) ||
          m.tags.some((t) => t.includes(searchTerm))
      );
    }
    if (sortBy === 'latest') {
      result.sort((a, b) => new Date(b.updateTime).getTime() - new Date(a.updateTime).getTime());
    } else if (sortBy === 'rating') {
      result.sort((a, b) => b.rating - a.rating);
    } else if (sortBy === 'views') {
      result.sort((a, b) => b.views - a.views);
    }
    setFilteredMovies(result);
  }, [activeCategory, searchTerm, sortBy]);

  useEffect(() => {
    const existing = document.getElementById('schema-category');
    if (existing) existing.remove();

    const schema = {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: `${currentCategory?.name || '分类'} - 91片库`,
      url: `https://www.91piianku.com/category/${activeCategory}`,
      description: `91片库${currentCategory?.name || '影视'}分类，海量高清${currentCategory?.name || '影视'}资源免费在线观看`,
      isPartOf: { '@type': 'WebSite', name: '91片库', url: 'https://www.91piianku.com' },
    };
    const script = document.createElement('script');
    script.id = 'schema-category';
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => { const el = document.getElementById('schema-category'); if (el) el.remove(); };
  }, [activeCategory, currentCategory]);

  return (
    <main className="min-h-screen pt-20 pb-16 bg-background-50">
      <div className="max-w-[1400px] mx-auto px-4 md:px-6">
        <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-6" aria-label="面包屑导航">
          <Link to="/" className="hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer">首页</Link>
          <span className="text-foreground-700">/</span>
          <span className="text-foreground-300">{currentCategory?.name || '分类'}</span>
        </nav>

        <div className="mb-8">
          <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-50 mb-2">
            {currentCategory?.name || '全部资源'}
          </h1>
          <p className="text-sm text-foreground-500">
            91片库为您精选{currentCategory?.name || '影视'}高清资源，免费在线观看
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2 mb-6 pb-6 border-b border-background-200/40">
          {categories.map((cat) => (
            <Link
              key={cat.slug}
              to={`/category/${cat.slug}`}
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm whitespace-nowrap transition-all duration-200 cursor-pointer ${
                activeCategory === cat.slug
                  ? 'bg-primary-500 text-background-950 font-medium'
                  : 'bg-background-100 text-foreground-400 hover:text-foreground-200 border border-background-200/40 hover:border-background-300/60'
              }`}
            >
              <i className={`${cat.icon} text-sm`}></i>
              {cat.name}
            </Link>
          ))}
        </div>

        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
          <div className="relative w-full sm:w-72">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={`搜索${currentCategory?.name || ''}资源...`}
              className="w-full pl-10 pr-4 py-2.5 text-sm bg-background-100 border border-background-200/50 rounded-lg text-foreground-200 placeholder-foreground-500 focus:outline-none focus:border-primary-500/50 transition-colors"
            />
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-500 text-sm"></i>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-foreground-500 whitespace-nowrap">排序：</span>
            {([
              { key: 'latest', label: '最新' },
              { key: 'rating', label: '评分' },
              { key: 'views', label: '人气' },
            ] as const).map((s) => (
              <button
                key={s.key}
                onClick={() => setSortBy(s.key)}
                className={`px-3 py-1.5 text-xs rounded-lg transition-colors whitespace-nowrap cursor-pointer ${
                  sortBy === s.key
                    ? 'bg-primary-500/20 text-primary-500 border border-primary-500/30'
                    : 'bg-background-100 text-foreground-500 border border-background-200/40 hover:text-foreground-300'
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 md:gap-4">
          {filteredMovies.length > 0 ? (
            filteredMovies.map((movie) => (
              <Link
                key={movie.id}
                to={`/article/${movie.id}`}
                className="group bg-background-100 border border-background-200/40 rounded-lg overflow-hidden hover:border-primary-500/30 transition-all duration-300 cursor-pointer"
                data-product-shop=""
              >
                <div className="relative aspect-[2/3] overflow-hidden">
                  <img
                    src={movie.poster}
                    alt={`${movie.title} - 91片库在线观看`}
                    title={`${movie.title} ${movie.year} ${movie.quality} - 91片库`}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-1.5 left-1.5 px-1.5 py-0.5 bg-primary-500 text-background-950 text-[10px] font-semibold rounded">
                    {movie.quality}
                  </div>
                  <div className="absolute top-1.5 right-1.5 px-1.5 py-0.5 bg-background-950/70 backdrop-blur-sm text-foreground-200 text-[10px] rounded">
                    {movie.rating}
                  </div>
                </div>
                <div className="p-2.5">
                  <h3 className="text-xs md:text-sm font-medium text-foreground-200 group-hover:text-primary-400 transition-colors line-clamp-1">
                    {movie.title}
                  </h3>
                  <p className="text-[10px] text-foreground-600 mt-1 line-clamp-1">{movie.year} · {movie.region}</p>
                </div>
              </Link>
            ))
          ) : (
            <div className="col-span-full flex flex-col items-center justify-center py-20 text-foreground-500">
              <i className="ri-movie-line text-4xl mb-4"></i>
              <p className="text-sm">没有找到相关资源</p>
              <p className="text-xs text-foreground-600 mt-1">试试其他关键词或分类</p>
            </div>
          )}
        </div>
      </div>
    </main>
  );
}